import React, { FC, useCallback } from 'react';
import { View } from 'react-native';
import ColorWrapper, { WithColor } from '@styles/colors';
import { isWeb } from '@helpers';
import { scale } from '@components/frame';
import { TEST_IDS } from '@v2/pages/common/constants/base.constants';
import { ActiveTool } from './types';
import { DRAWING_BOTTOM_BAR_HEIGHT } from './constants';
import AnnotationList from './components/AnnotationList';
import DrawingBottomToolbar from './components/DrawingBottomToolbar';
import DrawingTopBar from './components/DrawingTopBar';
import MobileAnnotationList from './components/MobileAnnotationList';
import AnnotationOverlay from './components/AnnotationOverlay';
import ZoomControls from '@v2/components/imageHandling/components/ProgressiveImage/ZoomControls';
import { useDrawingSessionContext } from './DrawingSessionContext';

interface DrawingCanvasProps extends WithColor {}

/**
 * Pure chrome — top bar, bottom toolbar, side / mobile annotation lists. State comes from the
 * lifted DrawingSession context (provided by ImageHandling). On web the annotation surface
 * (AnnotationOverlay) is rendered inline inside the drawing surface; on native it's rendered via
 * Picture's `zoomableOverlay` slot so it lives inside Zoomable.
 */
const DrawingCanvas: FC<DrawingCanvasProps> = ({ C }) => {
  const session = useDrawingSessionContext();
  const { api, transformController, selected, showFillToggle, handleLayout, layoutChrome, webContainerRef } = session;
  const { showMobileList, effectiveSidebarWidth, topBarHeight, safeTopInset, surfaceBottomInset, isMobileListExpanded, toggleMobileList } =
    layoutChrome;

  const handlePickTool = useCallback(
    (tool: ActiveTool) => {
      api.setActiveTool(tool);
    },
    [api]
  );

  return (
    <View testID={TEST_IDS.drawingCanvas.container} style={{ flex: 1, backgroundColor: 'transparent' }} pointerEvents="box-none">
      {/* Drawing surface — on web, hosts the annotation overlay + zoom controls. On native, the overlay
          lives inside Picture's Zoomable via slot prop, so this surface is empty. */}
      <View
        ref={(node) => {
          if (isWeb) webContainerRef.current = node as unknown as HTMLDivElement | null;
        }}
        style={{
          position: 'absolute',
          top: topBarHeight,
          left: 0,
          right: effectiveSidebarWidth > 0 ? scale(effectiveSidebarWidth) : 0,
          bottom: surfaceBottomInset,
          overflow: 'hidden',
        }}
        onLayout={handleLayout}
        pointerEvents="box-none"
      >
        {isWeb ? (
          <AnnotationOverlay
            bindControllerRef
            zoomControls={
              <ZoomControls C={C} scale={scale} zoom={transformController.zoom} onZoomIn={transformController.zoomIn} onZoomOut={transformController.zoomOut} />
            }
          />
        ) : null}
      </View>

      <View style={{ position: 'absolute', top: 0, left: 0, right: 0, height: topBarHeight, paddingTop: safeTopInset }}>
        <DrawingTopBar hasSelection={!!selected} onDone={api.handleDone} onCancel={api.handleCancel} onDeleteSelected={api.deleteSelected} />
      </View>

      {effectiveSidebarWidth > 0 ? (
        <View
          style={{
            position: 'absolute',
            top: topBarHeight,
            right: 0,
            bottom: scale(DRAWING_BOTTOM_BAR_HEIGHT),
            width: scale(effectiveSidebarWidth),
          }}
        >
          <AnnotationList annotations={api.annotations} selectedId={api.selectedId} onSelect={api.setSelectedId} widthPx={scale(effectiveSidebarWidth)} />
        </View>
      ) : null}

      {showMobileList ? (
        <View style={{ position: 'absolute', bottom: scale(DRAWING_BOTTOM_BAR_HEIGHT), left: 0, right: 0 }}>
          <MobileAnnotationList
            annotations={api.annotations}
            selectedId={api.selectedId}
            onSelect={api.setSelectedId}
            isExpanded={isMobileListExpanded}
            onToggleExpanded={toggleMobileList}
          />
        </View>
      ) : null}

      <View style={{ position: 'absolute', bottom: 0, left: 0, right: 0, height: scale(DRAWING_BOTTOM_BAR_HEIGHT) }}>
        <DrawingBottomToolbar
          activeTool={api.activeTool}
          strokeColor={api.strokeColor}
          strokeWidth={api.strokeWidth}
          fillActive={api.fillActive}
          canUndo={api.canUndo}
          canRedo={api.canRedo}
          showFillToggle={showFillToggle}
          onPickTool={handlePickTool}
          onPickColor={api.handlePickColor}
          onPickWidth={api.handlePickWidth}
          onToggleFill={api.handleToggleFill}
          onUndo={api.undo}
          onRedo={api.redo}
        />
      </View>
    </View>
  );
};

export default ColorWrapper(DrawingCanvas);
